<script>
    import { beforeNavigate, invalidate, onNavigate } from '$app/navigation';
    import { onMount } from 'svelte';
    import '../app.css';

    import {
        install as installAnalytics,
        save_params_of_interest
    } from '$lib/utils/logs.js';
    import Seo from '$lib/Seo.svelte';
    import SiteSchema from '$lib/SiteSchema.svelte';
    import { page, updated } from '$app/stores';
    import {
        discount_data,
        discount_checked,
        under_maintenance,
        cookie_consent_preferences
    } from '$lib/stores/globals';
    import { get } from 'svelte/store';
    import { install_paddle, is_paddle_blocked_path } from '$lib/utils/paddle';
    import DiscountBanner2 from '$lib/DiscountBanner2.svelte';
    import FacebookPixel from '$lib/FacebookPixel.svelte';
    import TwitterPixel from '$lib/TwitterPixel.svelte';
    // import DiscountBanner from '$lib/DiscountBanner.svelte';
    import ImageCarousel from '$lib/ImageCarousel.svelte';
    import FeedbackButton from '$lib/FeedbackButton.svelte';
    import FeedbackModal from '$lib/FeedbackModal.svelte';
    import CookieConsent from '$lib/CookieConsent.svelte';
    import { setup_global_mux_error_handling } from '$lib/utils/mux_player_error_handler';

    export let data;

    let { supabase, session } = data;
    $: ({ supabase, session } = data);

    beforeNavigate(({ to, willUnload }) => {
        if ($updated && willUnload && to?.url) {
            console.log('hard navigating to', to.url.href);
            location.href = to.url.href;
            return false;
        }
    });

    const disable_tracking_path_patterns = [
        /^\/escape\/?$/,
        /^\/join\/?$/,
        /^\/folder\/?$/,
        /^\/terminal\/?$/,
        /^\/phone\/?$/,
        /^\/minutes\/?$/,
        /^\/classrooms?(?:\/|$)/
    ];

    const is_tracking_disabled = (pathname = '') => {
        return disable_tracking_path_patterns.some((pattern) =>
            pattern.test(pathname)
        );
    };

    let discount_code = null;
    let show_feedback_modal = false;
    let disable_tracking = is_tracking_disabled($page.url.pathname);

    const schedule_paddle_install = (pathname = window.location.pathname) => {
        if (is_paddle_blocked_path(pathname)) return;

        const install = () =>
            install_paddle({ pathname: window.location.pathname });

        if ('requestIdleCallback' in window) {
            requestIdleCallback(install);
        } else {
            // Fallback for browsers that do not support requestIdleCallback
            setTimeout(install, 2000);
        }
    };

    onNavigate((navigation) => {
        const next_disable_tracking = is_tracking_disabled(
            navigation.to?.url?.pathname || window.location.pathname
        );
        const next_pathname =
            navigation.to?.url?.pathname || window.location.pathname;

        disable_tracking = next_disable_tracking;

        navigation.complete.then(() => {
            disable_tracking = next_disable_tracking;
            schedule_paddle_install(next_pathname);
        });
    });

    onMount(() => {
        save_params_of_interest();
        installAnalytics();
        const {
            data: { subscription }
        } = supabase.auth.onAuthStateChange((event, _session) => {
            // console.log('auth state change', event, _session);
            if (_session?.expires_at !== session?.expires_at) {
                // console.log('invalidating supabase:auth');
                invalidate('supabase:auth');
            }
        });

        if ($page.url.searchParams.get('d_id')) {
            discount_code = $page.url.searchParams.get('d_id');
        } else if ($page.url.pathname === '/jordan') {
            discount_code = 'JORDAN';
        } else if ($page.url.pathname === '/gifted') {
            discount_code = 'gifted';
        } else if ($page.url.pathname === '/repod') {
            discount_code = 'repod';
        } else {
            discount_code = localStorage.getItem('discount_code') || null;
        }

        if (discount_code) {
            if ($cookie_consent_preferences) {
                localStorage.setItem('discount_code', discount_code);
            }

            fetch('/api/paddle/check_code?code=' + discount_code)
                .then((res) => res.json())
                .then((dis) => {
                    if (dis.active) {
                        $discount_data = dis;
                    }
                })
                .catch((error) => console.warn('Discount check failed', error))
                .finally(() => ($discount_checked = true));
        } else {
            $discount_checked = true;
        }

        schedule_paddle_install();

        const disconnect_mux_observer = setup_global_mux_error_handling();

        return () => {
            subscription.unsubscribe();
            disconnect_mux_observer();
        };
    });
</script>

<svelte:head>
    <link rel="preconnect" href="https://um.critikid.com" />
    <link rel="preconnect" href="https://image.mux.com" />
    <link
        rel="preconnect"
        href="https://o4507029683109888.ingest.us.sentry.io"
    />
</svelte:head>

{#key $page.route}
    <Seo />
{/key}

<SiteSchema />

<div id="app-content" class="min-h-full">
    <slot />

    {#if !disable_tracking}
        <CookieConsent text_html={data.cookie_consent_html} />
    {/if}

    <FacebookPixel />
    <!-- <TwitterPixel /> -->

    <ImageCarousel />

    <DiscountBanner2 code="BLACKFRIDAY">
        Black Friday is here! Enjoy 30% off all courses.
    </DiscountBanner2>

    <DiscountBanner2 code="gifted">
        Welcome Gifted visitor, enjoy your 10% discount on all courses!
    </DiscountBanner2>

    <DiscountBanner2 code="repod">
        Welcome, Rethinking Education listener. Enjoy your 20% discount on all
        courses!
    </DiscountBanner2>

    {#if under_maintenance}
        <section
            class="fixed bottom-safe-offset-4 left-0 z-20 px-4 xs:px-8 max-w-full"
        >
            <div
                class="bg-yellow-300 border-yellow-500/50 text-yellow-950 p-4 rounded-xl relative shadow-md"
            >
                <p>
                    <b
                        >Our site is currently undergoing maintenance. Please
                        check back soon!</b
                    >
                </p>
                <p />
            </div>
        </section>
    {/if}
</div>

<div
    id="modal-target"
    class="fixed top-0 left-0 w-screen h-screen bg-black/5 h-screen-safe empty:hidden z-50"
/>

<!-- 
<DiscountBanner code="backtoschool">
    Back to school sale! 15% off all courses and worksheets with the code <strong>backtoschool</strong>
</DiscountBanner> -->
